import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db } from '../lib/firebase.js'
import {
  buyerKeyFromName,
  computeBuyBackCost,
  distinctBidders,
  placeCalcuttaBid,
  setBuyBackForBid,
} from '../lib/data.js'
import CLUB from '../config/club.js'
import PageNav from '../components/PageNav.jsx'

// Public bidder page — bidders scan one shared QR posted at the auction,
// enter their name once, then place bids on teams. Live: updates push as
// other bidders submit. If they get outbid, the row immediately re-flips.
//
//   /calcutta-bid?t={tournamentId}
//
const STORAGE_KEY = (tid) => `calcutta_bidder_${tid}`

export default function CalcuttaBidApp() {
  const [params] = useSearchParams()
  const tournamentId = params.get('t')

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')
  const [me, setMe] = useState(() => {
    if (!tournamentId) return null
    try {
      const raw = localStorage.getItem(STORAGE_KEY(tournamentId))
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the auction QR code from the host.')
      return
    }
    const off = onValue(ref(db, `tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  const setBidder = (name) => {
    const cleanName = (name || '').trim()
    if (!cleanName) return
    const buyerKey = buyerKeyFromName(cleanName)
    const next = { buyerName: cleanName, buyerKey }
    try { localStorage.setItem(STORAGE_KEY(tournamentId), JSON.stringify(next)) } catch {}
    setMe(next)
  }

  const switchUser = () => {
    try { localStorage.removeItem(STORAGE_KEY(tournamentId)) } catch {}
    setMe(null)
  }

  const settings = tournament?.calcutta?.settings || {}
  const enabled = !!settings.enabled

  return (
    <div className="min-h-full" style={{ backgroundColor: CLUB.backgroundColor }}>
      <PageNav back tone="gold" />
      <div className="max-w-md mx-auto px-4 pt-16 pb-10">
        <div className="text-center text-white">
          <h1 className="text-xl font-extrabold tracking-widest uppercase" style={{ color: CLUB.accentColor }}>
            ⛳ Calcutta Auction
          </h1>
          {tournament?.info?.name ? (
            <p className="text-sm opacity-80 mt-1">{tournament.info.name}</p>
          ) : null}
        </div>

        {error ? (
          <ErrorCard message={error} />
        ) : !tournament ? (
          <p className="text-white/70 text-center mt-10">Loading…</p>
        ) : !enabled ? (
          <ErrorCard message="The Calcutta auction for this tournament is not active." />
        ) : !me ? (
          <NameEntryScreen
            existing={distinctBidders(tournament.calcutta?.bids || {})}
            onSubmit={setBidder}
          />
        ) : (
          <BidScreen
            tournamentId={tournamentId}
            tournament={tournament}
            me={me}
            onSwitchUser={switchUser}
          />
        )}
      </div>
    </div>
  )
}

function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-2xl p-5 mt-10 text-center shadow-xl">
      <div className="text-3xl mb-2">⚠️</div>
      <p className="text-sm text-gray-700">{message}</p>
    </div>
  )
}

// ── Step 1 — name entry ────────────────────────────────────────────────────
function NameEntryScreen({ existing, onSubmit }) {
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [picked, setPicked] = useState('')

  const submit = (e) => {
    e?.preventDefault()
    if (picked) {
      const match = existing.find((b) => b.buyerKey === picked)
      if (match) onSubmit(match.buyerName)
      return
    }
    const full = `${first.trim()} ${last.trim()}`.trim()
    if (full) onSubmit(full)
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-5 mt-5 shadow-xl">
      <h2 className="text-lg font-extrabold text-center">Enter Your Name</h2>
      <p className="text-sm text-gray-500 text-center mt-1">
        Used to track bids on your phone and settle up after the auction.
      </p>

      <div className="mt-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <input
            className="input"
            placeholder="First name"
            value={first}
            onChange={(e) => { setFirst(e.target.value); setPicked('') }}
            autoFocus
            autoCapitalize="words"
          />
          <input
            className="input"
            placeholder="Last name"
            value={last}
            onChange={(e) => { setLast(e.target.value); setPicked('') }}
            autoCapitalize="words"
          />
        </div>

        {existing.length > 0 ? (
          <>
            <div className="flex items-center gap-2 my-2">
              <hr className="flex-1 border-gray-200" />
              <span className="text-sm uppercase tracking-widest text-gray-400 font-bold">or</span>
              <hr className="flex-1 border-gray-200" />
            </div>
            <select
              className="input"
              value={picked}
              onChange={(e) => { setPicked(e.target.value); setFirst(''); setLast('') }}>
              <option value="">— Returning bidder? Pick your name —</option>
              {existing.map((b) => (
                <option key={b.buyerKey} value={b.buyerKey}>{b.buyerName}</option>
              ))}
            </select>
          </>
        ) : null}

        <button
          type="submit"
          disabled={!picked && !`${first}${last}`.trim()}
          className="w-full rounded-xl py-3.5 font-extrabold uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-40"
          style={{
            background: `linear-gradient(180deg, ${CLUB.accentColor} 0%, ${CLUB.accentColorDark || CLUB.accentColor} 100%)`,
            color: CLUB.primaryColor,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
          }}>
          Start Bidding
        </button>
      </div>
    </form>
  )
}

// ── Step 2 — bid screen ───────────────────────────────────────────────────
function BidScreen({ tournamentId, tournament, me, onSwitchUser }) {
  const settings = tournament.calcutta?.settings || {}
  const bids = tournament.calcutta?.bids || {}
  const auctionOpen = settings.auctionOpen !== false
  const buyBackEnabled = !!settings.buyBackEnabled
  const buyBackPct = Number(settings.buyBackPct) || 0

  // Build the team list grouped by flight, Championship-first via flight ID
  // sort (flight00 = Championship by convention — see AdminApp create flow).
  const flights = useMemo(() => {
    const list = Object.entries(tournament.flights || {})
      .map(([id, f]) => ({
        id,
        name: f.info?.name || id,
        teams: Object.entries(f.teams || {}).map(([tid, t]) => ({
          teamId: tid,
          teamName: t.info?.name || tid,
          players: t.info?.players || [],
        })),
      }))
      .filter((f) => f.teams.length > 0)
    list.sort((a, b) => a.id.localeCompare(b.id))
    return list
  }, [tournament.flights])

  // My current ownership: every bid where I'm the high bidder.
  const myBids = useMemo(() => {
    const out = []
    for (const [teamId, b] of Object.entries(bids)) {
      if (b.buyerKey === me.buyerKey) out.push({ teamId, ...b })
    }
    return out
  }, [bids, me.buyerKey])

  const myTotal = myBids.reduce((n, b) => {
    const cost = (Number(b.bidAmount) || 0) + (b.buyBack ? Number(b.buyBackCost) || 0 : 0)
    return n + cost
  }, 0)
  const myBidsLookup = Object.fromEntries(myBids.map((b) => [b.teamId, b]))

  return (
    <>
      {/* Bidder identity strip */}
      <div className="bg-white rounded-2xl px-4 py-3 mt-5 shadow-xl flex justify-between items-center">
        <div className="min-w-0">
          <div className="text-sm uppercase tracking-widest text-gray-500 font-bold">Bidding as</div>
          <div className="font-extrabold truncate">{me.buyerName}</div>
        </div>
        <button
          className="text-sm text-gray-500 hover:text-green-deep font-semibold underline"
          onClick={onSwitchUser}>
          Switch user
        </button>
      </div>

      {!auctionOpen ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mt-3 text-center">
          <p className="text-sm font-bold text-amber-900">🔒 Auction is closed — no more bids</p>
        </div>
      ) : null}

      {flights.map((flight) => (
        <div key={flight.id} className="mt-4">
          <h3 className="text-sm uppercase tracking-widest font-extrabold mb-2 px-1" style={{ color: CLUB.accentColor }}>
            {flight.name}
          </h3>
          <div className="space-y-2">
            {flight.teams.map((t) => (
              <TeamBidRow
                key={t.teamId}
                tournamentId={tournamentId}
                team={t}
                flightName={flight.name}
                bid={bids[t.teamId]}
                me={me}
                auctionOpen={auctionOpen}
                buyBackEnabled={buyBackEnabled}
                buyBackPct={buyBackPct}
                isMine={!!myBidsLookup[t.teamId]}
              />
            ))}
          </div>
        </div>
      ))}

      <MySummaryCard
        myBids={myBids}
        myTotal={myTotal}
        buyBackEnabled={buyBackEnabled}
        flights={flights}
        auctionOpen={auctionOpen}
      />
    </>
  )
}

function TeamBidRow({ tournamentId, team, bid, me, auctionOpen, buyBackEnabled, buyBackPct, isMine }) {
  const currentAmount = Number(bid?.bidAmount) || 0
  const minBid = currentAmount > 0 ? currentAmount + 1 : 1
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null) // { kind: 'ok'|'err', msg }

  // Whenever the high bid moves, drop the draft so the placeholder updates.
  useEffect(() => { setDraft('') }, [currentAmount])

  const placeBid = async () => {
    const amt = Number(draft)
    if (!amt || amt <= currentAmount) {
      setFlash({ kind: 'err', msg: `Bid must be more than $${currentAmount}` })
      return
    }
    setBusy(true)
    try {
      const res = await placeCalcuttaBid(tournamentId, team.teamId, me.buyerName, amt)
      if (res.ok) {
        setFlash({ kind: 'ok', msg: `✓ You bid $${amt}` })
        setDraft('')
      } else if (res.reason === 'outbid') {
        const cur = res.current || {}
        setFlash({ kind: 'err', msg: `Outbid — current high $${cur.bidAmount} by ${cur.buyerName}` })
      } else if (res.reason === 'auction-closed') {
        setFlash({ kind: 'err', msg: 'Auction is closed' })
      } else {
        setFlash({ kind: 'err', msg: 'Could not place bid' })
      }
    } finally {
      setBusy(false)
      setTimeout(() => setFlash(null), 3500)
    }
  }

  const toggleBuyBack = async (next) => {
    const ok = await setBuyBackForBid(tournamentId, team.teamId, me.buyerKey, next, buyBackPct)
    if (!ok) setFlash({ kind: 'err', msg: 'Could not update buy-back — you may have been outbid' })
  }

  return (
    <div className={`bg-white rounded-2xl p-3 shadow ${isMine ? 'ring-2' : ''}`} style={isMine ? { '--tw-ring-color': CLUB.accentColor, boxShadow: `0 0 0 2px ${CLUB.accentColor}` } : {}}>
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <div className="font-extrabold truncate">{team.teamName}</div>
          {team.players.length > 0 ? (
            <div className="text-sm text-gray-500 truncate">{team.players.join(' · ')}</div>
          ) : null}
        </div>
        <div className="text-right shrink-0">
          {currentAmount > 0 ? (
            <>
              <div className="text-sm uppercase tracking-widest text-gray-500 font-bold">High Bid</div>
              <div className="font-extrabold">${currentAmount.toLocaleString()}</div>
              <div className="text-sm text-gray-500 truncate max-w-[120px]">
                {bid?.buyerName}{isMine ? ' (you)' : ''}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-400 italic">No bids yet</div>
          )}
        </div>
      </div>

      {auctionOpen ? (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm font-bold text-gray-500">$</span>
          <input
            type="number"
            min={minBid}
            inputMode="numeric"
            className="input flex-1"
            placeholder={`${minBid} or more`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button
            disabled={busy || !draft}
            onClick={placeBid}
            className="rounded-xl px-4 py-2 font-extrabold text-sm uppercase tracking-widest disabled:opacity-40"
            style={{ backgroundColor: CLUB.primaryColor, color: CLUB.accentColor }}>
            {busy ? '…' : 'Bid'}
          </button>
        </div>
      ) : null}

      {isMine && buyBackEnabled && auctionOpen ? (
        <label className="inline-flex items-center gap-2 text-sm mt-2 text-green-deep font-semibold">
          <input
            type="checkbox"
            checked={!!bid?.buyBack}
            onChange={(e) => toggleBuyBack(e.target.checked)}
          />
          Buy back this team (+${computeBuyBackCost(currentAmount, buyBackPct).toLocaleString()})
        </label>
      ) : null}

      {flash ? (
        <div className={`mt-2 text-sm font-bold ${flash.kind === 'ok' ? 'text-green-deep' : 'text-red-600'}`}>
          {flash.msg}
        </div>
      ) : null}
    </div>
  )
}

function MySummaryCard({ myBids, myTotal, buyBackEnabled, flights, auctionOpen }) {
  const teamLookup = useMemo(() => {
    const lk = {}
    for (const f of flights) {
      for (const t of f.teams) lk[t.teamId] = { teamName: t.teamName, flightName: f.name }
    }
    return lk
  }, [flights])

  const buyBackTotal = myBids.reduce((n, b) => n + (b.buyBack ? Number(b.buyBackCost) || 0 : 0), 0)
  const bidsTotal = myBids.reduce((n, b) => n + (Number(b.bidAmount) || 0), 0)

  return (
    <div className="bg-white rounded-2xl p-5 mt-5 shadow-xl">
      <div className="text-sm uppercase tracking-widest text-gray-500 font-bold">Your Teams</div>
      {myBids.length === 0 ? (
        <p className="text-sm text-gray-500 mt-2">
          No teams won yet. {auctionOpen ? 'Place a bid above!' : ''}
        </p>
      ) : (
        <>
          <ul className="divide-y divide-gray-100 mt-2">
            {myBids.map((b) => {
              const meta = teamLookup[b.teamId] || {}
              return (
                <li key={b.teamId} className="py-2 flex justify-between items-baseline gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{meta.teamName || b.teamId}</div>
                    <div className="text-sm text-gray-500 truncate">{meta.flightName || '—'}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">${Number(b.bidAmount).toLocaleString()}</div>
                    {buyBackEnabled && b.buyBack ? (
                      <div className="text-sm text-green-deep">+${Number(b.buyBackCost).toLocaleString()} BB</div>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
          <div className="border-t mt-2 pt-2 space-y-1 text-sm">
            <div className="flex justify-between"><span>Bids subtotal</span><span>${bidsTotal.toLocaleString()}</span></div>
            {buyBackEnabled && buyBackTotal > 0 ? (
              <div className="flex justify-between"><span>Buy-back</span><span>${buyBackTotal.toLocaleString()}</span></div>
            ) : null}
            <div className="flex justify-between border-t pt-1 mt-1">
              <span className="font-extrabold uppercase tracking-widest text-sm">Total Owed</span>
              <span className="font-extrabold text-green-deep text-base">${myTotal.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-sm text-gray-500 text-center mt-3">
            {auctionOpen ? 'Live total — outbids will adjust this instantly.' : 'Please pay at the Pro Shop. Status updates here when marked paid.'}
          </p>
        </>
      )}
    </div>
  )
}
